import {
    useGetValueNarrativesQuery,
    ValueMessage,
    ValueNarrative,
    ValueNarrativeCategory,
} from './queries/useGetValueNarrativesQuery'
import { useIsMarketMode } from '../../shared/hooks/useIsMarketMode'
import { useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

export const useValueNarratives = () => {
    const history = useHistory()
    const location = useLocation()
    const persistedLocationState = { autoOpenData: location.autoOpenData }

    const selectedCategoryId = location.pathname.split('/')[3]
    const selectedNarrativeId = location.pathname.split('/')[5]
    const selectedMessageId = location.pathname.split('/')[7]

    const { data, isLoading, isSuccess } = useGetValueNarrativesQuery()

    const { selectedMarket, isMarketMode } = useIsMarketMode()

    const dataWithoutPseudoCategories = useMemo(() => {
        return (data || [])
            .sort((a, b) => a.order - b.order)
            .map((category) => ({
                ...category,
                narratives: category.narratives.map((narrative) => {
                    return {
                        ...narrative,
                        valueMessages: narrative.valueMessages
                            .map((message) => {
                                if (!message) {
                                    return null
                                }

                                if (isMarketMode) {
                                    if (message.localMarkets.includes(selectedMarket)) {
                                        return message
                                    }
                                } else {
                                    if (!message.isCountrySpecific) {
                                        return message
                                    }
                                }

                                return null
                            })
                            .filter((message) => message !== null)
                            .sort((a, b) => a.order - b.order),
                    }
                }),
            }))
    }, [data, isMarketMode, selectedMarket])

    const allValueNarratives = dataWithoutPseudoCategories.reduce(
        (acc, category) => [...acc, ...category.narratives],
        []
    )

    const allArchivedValueNarratives = allValueNarratives.filter((narrative) => !!narrative.archived)
    const allNonArchivedValueNarratives = allValueNarratives.filter((narrative) => !narrative.archived)

    const dataWithPseudoCategories: ValueNarrativeCategory[] = [
        {
            _id: 'all',
            name: 'All',
            narratives: allNonArchivedValueNarratives,
            order: -1,
            isPseudoCategory: true,
        },
        ...dataWithoutPseudoCategories,
        {
            _id: 'archived',
            name: 'Archived',
            narratives: allArchivedValueNarratives,
            order: Number.MAX_SAFE_INTEGER,
            isPseudoCategory: true,
        },
    ]

    const selectedCategory: ValueNarrativeCategory = dataWithPseudoCategories?.find(
        (category) => category._id === selectedCategoryId
    )

    const handleSelectCategory = (categoryId: string, withReplace = false) => {
        const url = '/narratives/categories/' + categoryId
        const isIdenticalUrl = location.pathname === url

        if (withReplace || isIdenticalUrl) {
            history.replace({ pathname: url, ...persistedLocationState })
        } else {
            history.push({ pathname: url, ...persistedLocationState })
        }
    }

    const isInvalidCategoryId = !!data && !selectedCategory?._id

    useEffect(() => {
        if (isInvalidCategoryId) {
            handleSelectCategory('all', true)
        }
    }, [isInvalidCategoryId])

    const selectedNarrative: ValueNarrative | null = selectedCategory?.narratives.find(
        (narrative) => narrative._id === selectedNarrativeId
    )

    const handleSelectNarrative = (categoryId: string, narrativeId: string, withReplace = false) => {
        const url = '/narratives/categories/' + categoryId + '/narratives/' + narrativeId
        const isIdenticalUrl = location.pathname === url
        if (withReplace || isIdenticalUrl) {
            history.replace({ pathname: url, ...persistedLocationState })
        } else {
            history.push({ pathname: url, ...persistedLocationState })
        }
    }

    const isInvalidNarrativeId =
        !!data &&
        !!selectedCategory?._id &&
        selectedNarrativeId &&
        (selectedCategory.isPseudoCategory || !selectedNarrative?._id)

    useEffect(() => {
        if (isInvalidNarrativeId) {
            handleSelectCategory(selectedCategory._id, true)
        }
    }, [isInvalidNarrativeId, selectedCategory])

    const selectedMessage: ValueMessage | null = selectedNarrative?.valueMessages.find(
        (message) => message._id === selectedMessageId
    )

    const resolvedAutoOpenMessage = useMemo(() => {
        const autoOpenValueMessage = location.autoOpenData?.autoOpenData as Partial<ValueMessage> | undefined
        const autoOpenMessageId = autoOpenValueMessage?._id

        if (!autoOpenMessageId) {
            return null
        }

        for (const category of dataWithoutPseudoCategories) {
            for (const narrative of category.narratives) {
                const matchedMessage = narrative.valueMessages.find((message) => message?._id === autoOpenMessageId)

                if (matchedMessage) {
                    return matchedMessage
                }
            }
        }

        return null
    }, [dataWithoutPseudoCategories, location.autoOpenData?.autoOpenData])

    const handleSelectMessage = (
        categoryId: string,
        narrativeId: string,
        messageId: string,
        withReplace: boolean = false
    ) => {
        const url = '/narratives/categories/' + categoryId + '/narratives/' + narrativeId + '/messages/' + messageId
        const isIdenticalUrl = location.pathname === url

        if (withReplace || isIdenticalUrl) {
            history.replace({ pathname: url, ...persistedLocationState })
        } else {
            history.push({ pathname: url, ...persistedLocationState })
        }
    }

    const isInvalidMessageId =
        !!data && !!selectedCategory?._id && !!selectedNarrative?._id && selectedMessageId && !selectedMessage?._id

    useEffect(() => {
        if (isInvalidMessageId) {
            handleSelectNarrative(selectedCategory._id, selectedNarrative._id, true)
        }
    }, [isInvalidMessageId, selectedNarrative])

    const isCategoriesDataReady = !!selectedCategory && !isLoading && isSuccess

    useEffect(() => {
        if (!isCategoriesDataReady) return

        if (location?.autoOpenData?.isAutoOpenPanel && location.autoOpenData?.autoOpenData) {
            const valueMessage = location.autoOpenData?.autoOpenData as ValueMessage | undefined

            const messageId = valueMessage?._id
            const categoryId = valueMessage?.categoryId || resolvedAutoOpenMessage?.categoryId
            const narrativeId = valueMessage?.narrativeId || resolvedAutoOpenMessage?.narrativeId

            if (messageId && categoryId && narrativeId) {
                handleSelectMessage(categoryId, narrativeId, messageId, true)
            } else {
                location.autoOpenData = null
            }
        }
    }, [
        isCategoriesDataReady,
        location?.autoOpenData?.isAutoOpenPanel,
        location.autoOpenData?.autoOpenData,
        resolvedAutoOpenMessage?.categoryId,
        resolvedAutoOpenMessage?.narrativeId,
    ])

    return {
        dataWithPseudoCategories,
        dataWithoutPseudoCategories,

        selectedCategory,
        handleSelectCategory,

        selectedNarrative,
        handleSelectNarrative,

        handleSelectMessage,

        isCategoriesDataReady,
    }
}
